import { useSelection } from "./useSelection";
import { BulkAction } from "../ui/Table";

export interface UseBulkActionsOptions<T> {
	items: T[],
	idProp?: string,
	onDelete: (items: T[]) => Promise<any>,
	onDuplicate?: (item: T) => void,
	onReplace?: (item: T) => void,
}

export function useBulkActions<T>(opts: UseBulkActionsOptions<T>) {
	const { items, idProp = "id", onDelete, onDuplicate, onReplace } = opts;
	const selection = useSelection(idProp);

	function getSelectedItems(): T[] {
		return Array.from(selection.selected)
			.map(id => items.find((r: any) => r[idProp] === id))
			.filter(Boolean) as T[];
	}

	const bulkActions: BulkAction[] | undefined = selection.selected.size > 0 ? [
		...(selection.selected.size === 1 && onDuplicate ? [{ label: "Duplicate", action: "duplicate" }] : []),
		...(selection.selected.size === 1 && onReplace ? [{ label: "Replace", action: "replace" }] : []),
		{ label: "Delete", action: "delete", className: "error" },
	] : undefined;

	function onBulkAction(action: string) {
		if (action === "duplicate" && onDuplicate) {
			const selectedItems = getSelectedItems();
			if (selectedItems.length === 1) {
				onDuplicate(selectedItems[0]);
			}
		}
		if (action === "replace" && onReplace) {
			const selectedItems = getSelectedItems();
			if (selectedItems.length === 1) {
				onReplace(selectedItems[0]);
			}
		}
		if (action === "delete") {
			const selectedItems = getSelectedItems();
			if (selectedItems.length > 0) {
				onDelete(selectedItems).then(() => {
					selection.clear();
				});
			}
		}
	}

	return {
		selection,
		bulkActions,
		onBulkAction,
		getSelectedItems,
	};
}
